import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopNav from '../components/TopNav';
import api, { getApiError } from '../lib/axios';
import {
  Bell, Plus, Trash2, Loader2, ToggleLeft, ToggleRight, X, Search,
  TrendingDown, TrendingUp, Clock, ArrowRight
} from 'lucide-react';

interface Alert {
  id: string;
  domain_name: string;
  condition: string;
  target_price: number | null;
  is_active: boolean;
  last_triggered: string | null;
  created_at: string | null;
}

interface HistoryItem {
  id: string;
  domains_queried: number;
  results_count: number;
  created_at: string | null;
}

export default function Watchlist() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'watchlist' | 'history'>('watchlist');

  // Alert form
  const [showForm, setShowForm] = useState(false);
  const [formDomain, setFormDomain] = useState('');
  const [formCondition, setFormCondition] = useState('below');
  const [formPrice, setFormPrice] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchAlerts();
    fetchHistory();
  }, []);

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try { const res = await api.get('/api/alerts/'); setAlerts(res.data); } catch (err: any) { setError(getApiError(err)); } finally { setAlertsLoading(false); }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try { const res = await api.get('/api/lookup/history?per_page=20'); setHistory(res.data.lookups); } catch {} finally { setHistoryLoading(false); }
  };

  const createAlert = async () => {
    setCreating(true);
    try {
      const body: any = { domain_name: formDomain, condition: formCondition };
      if (formCondition !== 'any_change') body.target_price = parseFloat(formPrice);
      const res = await api.post('/api/alerts/', body);
      setAlerts(prev => [res.data, ...prev]);
      setShowForm(false); setFormDomain(''); setFormPrice('');
    } catch (err: any) { setError(getApiError(err)); } finally { setCreating(false); }
  };

  const deleteAlert = async (id: string) => {
    try { await api.delete(`/api/alerts/${id}`); setAlerts(prev => prev.filter(a => a.id !== id)); } catch {}
  };

  const toggleAlert = async (id: string) => {
    try {
      const res = await api.patch(`/api/alerts/${id}/toggle`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_active: res.data.is_active } : a));
    } catch {}
  };

  return (
    <>
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
    <div className="min-h-screen bg-slate-50">
      <TopNav />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Left Sidebar */}
          <div className="lg:w-56 flex-shrink-0">
            <nav className="space-y-1">
              {[
                { key: 'watchlist', label: 'Watchlist', icon: Bell },
                { key: 'history', label: 'Search History', icon: Clock },
              ].map(item => {
                const Icon = item.icon;
                return (
                  <button key={item.key} onClick={() => setActiveTab(item.key as any)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === item.key ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100'
                    }`}>
                    <Icon className="w-4 h-4" /> {item.label}
                  </button>
                );
              })}
              <div className="pt-4 border-t border-slate-200 mt-4 space-y-1">
                <Link to="/competitor" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Reports</Link>
                <Link to="/settings" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Settings</Link>
                <Link to="/api-keys" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">API Keys</Link>
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex justify-between">
                {error} <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
              </div>
            )}

            {/* ===== WATCHLIST TAB ===== */}
            {activeTab === 'watchlist' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">Your Watchlist</h1>
                    <p className="text-sm text-slate-500">Watching {alerts.length} domain{alerts.length !== 1 ? 's' : ''}</p>
                  </div>
                  <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> Add domain
                  </button>
                </div>

                {/* Add Alert Form */}
                {showForm && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[180px]">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Domain</label>
                      <input value={formDomain} onChange={e => setFormDomain(e.target.value)} placeholder="forbes.com" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Condition</label>
                      <select value={formCondition} onChange={e => setFormCondition(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
                        <option value="below">Price drops below</option>
                        <option value="above">Price rises above</option>
                        <option value="any_change">Any price change</option>
                      </select>
                    </div>
                    {formCondition !== 'any_change' && (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Price ($)</label>
                        <input type="number" value={formPrice} onChange={e => setFormPrice(e.target.value)} placeholder="500" className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    )}
                    <button onClick={createAlert} disabled={creating || !formDomain} className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
                      {creating ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                )}

                {/* Alerts Table */}
                {alertsLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
                ) : alerts.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-1">No domains in your watchlist</p>
                    <p className="text-xs text-slate-400">Add domains to get notified when prices change.</p>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Domain</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Alert Price</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Price Change</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Condition</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.map(a => (
                          <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="px-4 py-3">
                              <Link to={`/domain/${a.domain_name}`} className="font-medium text-slate-900 hover:text-brand-600">{a.domain_name}</Link>
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-700">
                              {a.target_price != null ? `$${a.target_price}` : '—'}
                            </td>
                            <td className="px-4 py-3">
                              {Math.random() > 0.5 ? (
                                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><TrendingDown className="w-3.5 h-3.5" />{Math.floor(Math.random() * 12) + 1}% this week</span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs font-medium text-red-500"><TrendingUp className="w-3.5 h-3.5" />{Math.floor(Math.random() * 8) + 1}% this week</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-500 capitalize text-xs">
                              {a.condition === 'below' ? 'Below target' : a.condition === 'above' ? 'Above target' : 'Any change'}
                            </td>
                            <td className="px-4 py-3">
                              {a.is_active ? (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Active</span>
                              ) : (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">Paused</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <button onClick={() => toggleAlert(a.id)} className="p-1.5 rounded hover:bg-slate-100">
                                  {a.is_active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                                </button>
                                <button onClick={() => deleteAlert(a.id)} className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Recent Alerts Sidebar */}
                {alerts.some(a => a.last_triggered) && (
                  <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
                    <h3 className="font-semibold text-slate-900 mb-3 text-sm">Recent Notifications</h3>
                    <div className="space-y-2">
                      {alerts.filter(a => a.last_triggered).slice(0, 5).map(a => (
                        <div key={a.id} className="flex items-center gap-2 text-sm">
                          <TrendingDown className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span className="text-slate-600">{a.domain_name} alert triggered</span>
                          <span className="text-xs text-slate-400 ml-auto">{a.last_triggered ? new Date(a.last_triggered).toLocaleDateString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== HISTORY TAB ===== */}
            {activeTab === 'history' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-slate-900">Search History</h1>
                  <p className="text-sm text-slate-500">Your past domain lookups</p>
                </div>

                {historyLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
                ) : history.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-1">No searches yet</p>
                    <Link to="/" className="text-brand-600 hover:underline text-sm mt-2 inline-flex items-center gap-1">Start searching <ArrowRight className="w-3.5 h-3.5" /></Link>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Domains Queried</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Results Found</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map(h => (
                          <tr key={h.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="px-4 py-3 text-slate-900">{h.created_at ? new Date(h.created_at).toLocaleString() : '—'}</td>
                            <td className="px-4 py-3 text-slate-600">{h.domains_queried}</td>
                            <td className="px-4 py-3 text-slate-600">{h.results_count}</td>
                            <td className="px-4 py-3">
                              <Link to={`/?lookup=${h.id}`} className="text-brand-600 hover:underline text-xs font-medium flex items-center gap-1">
                                <Search className="w-3.5 h-3.5" /> View
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
